import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { useCustomers } from '@/hooks/useCustomers';
import { usePagination } from '@/hooks/usePagination';
import type { Customer } from '@/types';

const columns: DataTableColumn<Customer>[] = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    render: (item) => (
      <span className="text-sm text-foreground">
        {[item.first_name, item.last_name].filter(Boolean).join(' ') || '\u2014'}
      </span>
    ),
  },
  {
    key: 'email',
    label: 'Email',
    sortable: true,
    className: 'w-[220px]',
    render: (item) => (
      <span className="text-sm text-muted-foreground">{item.email ?? '\u2014'}</span>
    ),
  },
  {
    key: 'phone',
    label: 'Phone',
    className: 'w-[140px]',
    render: (item) => (
      <span className="text-xs text-muted-foreground">{item.phone ?? '\u2014'}</span>
    ),
  },
  {
    key: 'orders_count',
    label: 'Orders',
    sortable: true,
    className: 'w-[80px]',
    render: (item) => <span className="text-sm font-mono text-foreground">{item.orders_count}</span>,
  },
  {
    key: 'total_spent',
    label: 'Total Spent',
    sortable: true,
    className: 'w-[110px]',
    render: (item) => (
      <span className="text-sm font-mono text-foreground">{formatCurrency(item.total_spent)}</span>
    ),
  },
  {
    key: 'state',
    label: 'Status',
    className: 'w-[90px]',
    render: (item) => {
      const variant =
        item.state === 'enabled' ? 'success' : item.state === 'disabled' ? 'destructive' : 'secondary';
      return item.state ? (
        <Badge variant={variant} className="text-[10px] capitalize">
          {item.state}
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">{'\u2014'}</span>
      );
    },
  },
];

function customerDisplayName(item: Customer) {
  return [item.first_name, item.last_name].filter(Boolean).join(' ') || '—';
}

function CustomerMobileCard({ item }: { item: Customer }) {
  const variant =
    item.state === 'enabled' ? 'success' : item.state === 'disabled' ? 'destructive' : 'secondary';

  return (
    <div className="space-y-3">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{customerDisplayName(item)}</p>
          <p className="mt-0.5 break-words text-xs text-muted-foreground">
            {item.email ?? 'No email'}
          </p>
          {item.phone && (
            <p className="mt-0.5 text-xs text-muted-foreground">{item.phone}</p>
          )}
        </div>
        {item.state ? (
          <Badge variant={variant} className="shrink-0 text-[10px] capitalize">
            {item.state}
          </Badge>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md border border-border bg-muted/30 p-2">
          <p className="mb-1 text-[10px] font-medium uppercase text-muted-foreground">Orders</p>
          <p className="text-sm font-mono text-foreground">{item.orders_count}</p>
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-2">
          <p className="mb-1 text-[10px] font-medium uppercase text-muted-foreground">Spent</p>
          <p className="text-sm font-mono text-foreground">{formatCurrency(item.total_spent)}</p>
        </div>
      </div>
    </div>
  );
}

export function CustomersPage() {
  const navigate = useNavigate();
  const {
    page,
    perPage,
    search,
    sort,
    setPage,
    setSearch,
    setSort,
    getSearchPath,
  } = usePagination({
    initialSort: { field: 'total_spent', direction: 'desc' },
  });

  const { data, isLoading, isFetching } = useCustomers({
    page,
    per_page: perPage,
    search,
    sort_by: sort.field,
    sort_dir: sort.direction,
  });

  return (
    <div className="h-full flex flex-col">
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        total={data?.total ?? 0}
        page={page}
        perPage={perPage}
        sort={sort}
        search={search}
        isLoading={isLoading}
        isFetching={isFetching}
        getRowId={(item) => item.id}
        onPageChange={setPage}
        onSortChange={setSort}
        onSearchChange={setSearch}
        onRowClick={(item) =>
          navigate(getSearchPath(`/customers/${item.shopify_customer_id}`))
        }
        searchPlaceholder="Search by name, email, phone..."
        emptyMessage="No customers found"
        mobileRender={(item) => <CustomerMobileCard item={item} />}
      />
    </div>
  );
}
